<script lang="ts">
	import type { CartState } from '../../handlers/cart_state.svelte';

	import {
		GeolocateControl,
		MapLibre,
		Marker,
		NavigationControl,
		GeoJSONSource,
		FillLayer,
		LineLayer
	} from 'svelte-maplibre-gl';

	import ButtonAsync from '$lib/utils/components/ButtonAsync.svelte';
	import LucideIcon from '$lib/utils/components/LucideIcon.svelte';
	import { Textarea } from '$lib/components/ui/textarea';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';

	import { utils } from '$lib/utils/utils.svelte';
	import { Loc } from '$lib/tools/locale.svelte';
	import { toast } from '$lib/utils/toast';
	import maplibregl from 'maplibre-gl';
	import { onMount } from 'svelte';
	import { root_modal_state } from '$lib/utils/states/root_modal_state.svelte';

	onMount(() => {
		const pluginStatus = maplibregl.getRTLTextPluginStatus() as 'unavailable' | 'loading' | 'loaded' | 'error';
		if (pluginStatus !== 'loaded')
			maplibregl.setRTLTextPlugin(
				'https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.3.0/dist/mapbox-gl-rtl-text.js',
				true
			);
	});

	let {
		selected_location = $bindable(null),
		address_description = $bindable(''),
		cart_state
	}: {
		selected_location: { lat: number; lng: number } | null;
		address_description: string;
		cart_state: CartState;
	} = $props();

	let map: maplibregl.Map | undefined = $state();
	let google_maps_link = $state('');
	let click_timeout = $state<NodeJS.Timeout>();

	async function validate_and_set_location(lat: number, lng: number, fly = false) {
		if (!utils.addresses.is_point_in_polygon(lng, lat, cart_state.variables.seller?.shipping_geofence)) {
			root_modal_state.open_confirm_modal({
				subtitle: 'confirm_insert_location_out_of_bounds',
				onsuccess: () => {
					selected_location = { lat, lng };
					toast.success('location_set_success');
					if (fly) map?.flyTo({ center: [lng, lat], zoom: 14 });
				},
				ondecline: () => {
					toast.error(Loc('location_out_of_bounds'));
				}
			});
		} else {
			selected_location = { lat, lng };
			toast.success('location_set_success');
			if (fly) map?.flyTo({ center: [lng, lat], zoom: 14 });
		}
	}

	async function parse_link() {
		try {
			if (!google_maps_link) return false;
			const coords = await utils.addresses.parse_location_share_url(google_maps_link);
			if (!coords) throw 'extract_failed';

			validate_and_set_location(coords.latitude, coords.longitude, true);
			return true;
		} catch (error) {
			console.error(error);
			toast.error('unable_to_extract_location');
			return false;
		}
	}

	async function paste_link() {
		try {
			google_maps_link = await navigator.clipboard.readText();
			return parse_link();
		} catch (e) {
			toast.error(Loc('unable_to_extract_location'));
			return false;
		}
	}

	function onclick_handler(e: maplibregl.MapMouseEvent) {
		if (click_timeout) {
			clearTimeout(click_timeout);
			click_timeout = undefined;
			return;
		}
		click_timeout = setTimeout(() => {
			validate_and_set_location(e.lngLat.lat, e.lngLat.lng);
			click_timeout = undefined;
		}, 200);
	}
	// $inspect(cart_state.variables.seller, selected_location);
</script>

<div class="space-y-4">
	<div class="flex gap-2 items-end">
		<div class="flex-1 space-y-1">
			<div class="flex justify-between">
				<Label class="text-xs text-muted-foreground">{Loc('google_maps_link')}</Label>
				<span class="text-[10px] text-muted-foreground border px-1 rounded">{Loc('optional')}</span>
			</div>
			<Input dir="ltr" bind:value={google_maps_link} onchange={parse_link} placeholder="https://maps.app.goo.gl/..." />
		</div>
		<ButtonAsync variant="outline" size="icon" onclick={paste_link}
			><LucideIcon name="Clipboard" size="16" /></ButtonAsync
		>
	</div>

	<div
		class="relative h-[300px] w-full rounded border overflow-hidden {selected_location
			? 'border-foreground shadow-sm'
			: 'border-border'}"
	>
		<MapLibre
			bind:map
			center={[13.1767, 32.7875]}
			zoom={9}
			attributionControl={false}
			doubleClickZoom={true}
			trackResize={true}
			scrollZoom={false}
			onload={(event) => {
				event.target?.resize?.();
			}}
			class="h-full w-full"
			style="https://tiles.openfreemap.org/styles/liberty"
			onclick={onclick_handler}
		>
			<GeolocateControl />
			<NavigationControl />
			<NavigationControl position="top-left" />
			<GeoJSONSource
				id="area"
				data={cart_state.variables.seller?.shipping_geofence || utils.addresses.envs.SERVICE_AREA_GEOJSON}
			>
				<FillLayer paint={{ 'fill-color': '#000000', 'fill-opacity': 0.1 }} />
				<LineLayer paint={{ 'line-color': '#000000', 'line-width': 1, 'line-dasharray': [2, 2] }} />
			</GeoJSONSource>
			{#if selected_location}
				<Marker lnglat={selected_location}>
					{#snippet content()}
						<LucideIcon name="MapPin" class="text-black drop-shadow-lg fill-white" size="32" />
					{/snippet}
				</Marker>
			{/if}
		</MapLibre>
		{#if !selected_location}
			<div
				class="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-xs backdrop-blur-sm pointer-events-none"
			>
				{Loc('click_map_to_select')}
			</div>
		{/if}
	</div>

	<div class="space-y-1">
		<div class="flex justify-between">
			<Label>{Loc('address_description')}</Label>
			<span class="text-[10px] text-muted-foreground border px-1 rounded">{Loc('optional')}</span>
		</div>
		<Textarea
			bind:value={address_description}
			class="resize-none h-20"
			placeholder={Loc('address_description_extra_details')}
		/>
	</div>
</div>
